import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  uploadDrug,
  updateDrug,
  fetchDrugCategories
} from '../../redux/middleware/medication/drugs/drugs';
import { currencyMapping } from '../../utils/currencyData';

const DrugUploadForm = ({
  open,
  handleClose,
  isEditMode = false,
  drugData = null,
  onSubmit
}) => {
  const initialDrugState = {
    name: '',
    category_ids: [],
    description: '',
    purpose: '',
    treating_diseases: '',
    how_it_works: '',
    dosage_form: '',
    side_effects: '',
    important_info: '',
    special_precautions: '',
    price: '',
    cost_price: '',
    currency: '',
    stock: '',
    expiration_date: '',
    manufacturer: '',
    sku: '',
    barcode: '',
    batch_number: '',
    unit_size: '',
    storage_instructions: '',
    packaging_info: '',
    conditions_treated: '',
    contraindications: '',
    brand_name: '',
    reorder_level: '10',
    image: null
  };

  const [drug, setDrug] = useState(initialDrugState);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' // 'success', 'error', 'warning', 'info'
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const [success, setSuccess] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [isConverting, setIsConverting] = useState(false);

  // Add conversion function
  const convertPrice = async (price, cost_price, currency) => {
    try {
      const response = await fetch('/medication/drug/convert_currency/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price, cost_price, currency })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      return data;
    } catch (error) {
      console.error('Price conversion failed:', error);
      return null;
    }
  };

  // Add category mapping state
  const [categoryMap, setCategoryMap] = useState({});

  // Update useEffect to create mapping
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await dispatch(fetchDrugCategories());
        setCategories(fetchedCategories || []);

        // Create UUID to ID mapping
        const mapping = {};
        fetchedCategories.forEach((cat, index) => {
          mapping[cat.id] = index + 1;
        });
        setCategoryMap(mapping);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchCategories();
  }, [dispatch]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setDrug((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size <= 5 * 1024 * 1024) {
        setDrug((prev) => ({
          ...prev,
          image: file
        }));
        setImagePreview(URL.createObjectURL(file));
      } else {
        setError('Image file size must be less than 5MB');
      }
    } else {
      setError('Please upload a valid image file');
    }
  }, []);

  // Add state for selected category
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [touched, setTouched] = useState(false);

  // Update category change handler
  const handleCategoryChange = useCallback(
    (e) => {
      setTouched(true);
      const selectedUUID = e.target.value;
      const numericId = categoryMap[selectedUUID];
      const selectedCat = categories.find((cat) => cat.id === selectedUUID);

      setSelectedCategory(selectedCat);
      setDrug((prev) => ({
        ...prev,
        category_ids: numericId ? [numericId] : []
      }));
    },
    [categoryMap, categories]
  );

  const validateForm = useCallback(() => {
    const requiredFields = [
      'name',
      'description',
      'price',
      'cost_price',
      'stock',
      'expiration_date',
      'conditions_treated',
      'contraindications',
      'brand_name',
      'packaging_info'
    ];

    const missingFields = requiredFields.filter((field) => !drug[field]);
    if (missingFields.length > 0) {
      setError(
				`Please fill in all required fields: ${missingFields.join(', ')}`
      );
      return false;
    }

    if (!drug.category_ids.length) {
      setError('Please select a category');
      return false;
    }

    if (Number(drug.price) < 0) {
      setError('Price must be a positive number');
      return false;
    }

    if (Number(drug.cost_price) < 0) {
      setError('Cost price must be a positive number');
      return false;
    }

    if (Number(drug.price) < Number(drug.cost_price)) {
      setError(
        'Selling price should not be less than cost price (to avoid selling at a loss)'
      );
      return false;
    }

    if (Number(drug.stock) < 0) {
      setError('Stock cannot be negative');
      return false;
    }

    if (new Date(drug.expiration_date) < new Date()) {
      setError('Expiration date must be a future date');
      return false;
    }

    return true;
  }, [drug]);

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Add this useEffect to initialize form data in edit mode
  useEffect(() => {
    if (isEditMode && drugData) {
      console.log('Initializing form with existing drug data:', drugData);

      // Map API data to form structure explicitly
      const formattedDrugData = {
        // Only include fields that match your form
        name: drugData.name || '',
        category_ids: [],
        description: drugData.description || '',
        purpose: drugData.purpose || '',
        conditions_treated: drugData.conditions_treated || '',
        dosage_form: drugData.dosage_form || '',
        side_effects: drugData.side_effects || '',
        important_info: drugData.important_info || '',
        special_precautions: drugData.special_precautions || '',
        price: drugData.price || '',
        cost_price: drugData.cost_price || '',
        currency:
					drugData.currency || drugData.price_info?.original_currency || '',
        stock: drugData.stock?.toString() || '',
        expiration_date: drugData.expiration_date || '',
        manufacturer: drugData.manufacturer || '',
        sku: drugData.sku || '',
        barcode: drugData.barcode || '',
        batch_number: drugData.batch_number || '',
        unit_size: drugData.unit_size || '',
        storage_instructions: drugData.storage_instructions || '',
        packaging_info: drugData.packaging_info || '',
        contraindications: drugData.contraindications || '',
        brand_name: drugData.brand_name || '',
        reorder_level: drugData.reorder_level?.toString() || '10',

        // Special fields
        id: drugData.id, // Keep ID for API calls
        public_id: drugData.public_id, // Keep public_id for API calls
        category_ids:
					drugData.categories?.map((cat) => {
					  // Use the numeric ID from categoryMap if available
					  return categoryMap[cat.id] || cat.id;
					}) || [],

        // Don't set image directly - let user upload a new one if needed
        image: null
      };

      // Update form state with clean data
      setDrug(formattedDrugData);

      // Set image preview if available
      if (drugData.imageUrl || drugData.img) {
        setImagePreview(drugData.imageUrl || drugData.img);
      }

      // Set selected category if available
      if (drugData.categories && drugData.categories.length > 0) {
        const category = drugData.categories[0];
        setSelectedCategory(category);
      }
    }
  }, [isEditMode, drugData]);

  // Update handleSubmit to ensure success handling works

  // Update handleSubmit to handle both create and update
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!validateForm()) {
        setLoading(false);
        return;
      }

      try {
        // Convert both prices
        setIsConverting(true);
        const priceInfo = await convertPrice(
          drug.price,
          drug.cost_price,
          drug.currency
        );
        setIsConverting(false);

        const formData = new FormData();

        // Append all form fields
        Object.entries(drug).forEach(([key, value]) => {
          // Skip id in edit mode (handled separately)
          if (key === 'id' || key === 'public_id') return;

          if (key === 'image') {
            // Only append image if it's a File object (new image)
            if (value instanceof File) formData.append('image', value);
          } else if (
            key === 'category_ids' &&
						Array.isArray(value) &&
						value.length > 0
          ) {
            // Ensure we're sending numeric IDs
            // If the IDs are already numeric, this won't change anything
            const numericId = Number(value[0]);
            if (!isNaN(numericId)) {
              formData.append('category_ids', numericId);
            } else {
              // Try to use the mapping
              const mappedId = categoryMap[value[0]];
              if (mappedId) {
                formData.append('category_ids', mappedId);
              }
            }
          } else if (key === 'currency') {
            formData.append('currency', value.code || value);
          } else if (value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });

        if (priceInfo) {
          formData.append('price_info', JSON.stringify(priceInfo));
        }

        let response;
        let drugData = null;

        // Use direct fetch API instead of Redux for updates
        if (isEditMode && drug.id) {
          console.log('Updating drug with ID:', drug.id);
          const updateResponse = await fetch(`/medication/drug/${drug.id}/`, {
            method: 'PATCH',
            body: formData
          });

          if (!updateResponse.ok) {
            throw new Error(`Update failed: ${updateResponse.statusText}`);
          }

          drugData = await updateResponse.json();
          response = { data: drugData };
        } else {
          // Use Redux for creates only
          response = await dispatch(uploadDrug(formData));

          // Extract drug data from Redux response
          if (response?.data) {
            drugData = response.data;
          } else if (response?.payload) {
            drugData = response.payload;
          }

          if (response?.error) {
            throw new Error(response.error.message || 'Upload failed');
          }
        }

        // Success message
        const successMsg = isEditMode
          ? 'Drug updated successfully!'
          : 'Drug uploaded successfully!';
        setSuccess(successMsg);

        // Show success snackbar
        setSnackbar({
          open: true,
          message: successMsg,
          severity: 'success'
        });

        // Reset form in create mode only
        if (!isEditMode) {
          setDrug(initialDrugState);
          setImagePreview(null);
        }

        // Trigger refresh event - safely using drugData
        if (drugData) {
          window.dispatchEvent(
            new CustomEvent('drugUpdated', {
              detail: {
                action: isEditMode ? 'updated' : 'added',
                drug: drugData
              }
            })
          );
        } else {
          console.warn('No drug data available for event dispatch');
        }

        // Call onSubmit callback if provided
        if (onSubmit) onSubmit();

        // Close modal with timeout
        if (handleClose) {
          setTimeout(() => handleClose(), 2000);
        }
      } catch (error) {
        console.error('Error:', error);
        setError(
          error.message || `Drug ${isEditMode ? 'update' : 'upload'} failed`
        );
        setSnackbar({
          open: true,
          message:
						error.message || `Drug ${isEditMode ? 'update' : 'upload'} failed`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    },
    [
      dispatch,
      drug,
      handleClose,
      validateForm,
      isEditMode,
      onSubmit,
      categoryMap,
      initialDrugState
    ]
  );

  return (
    <>
      <Box sx={{ width: '100%', backgroundColor: 'white' }}>
        <Container sx={{ p: 2, backgroundColor: 'white', color: 'black' }}>
          <Card
            sx={{
						  backgroundColor: 'white',
						  color: 'black',
						  border: '2px solid #ccc'
            }}
          >
            <CardContent>
              <Typography
                variant='h4'
                align='center'
                fontWeight='bold'
                gutterBottom
              >
                Upload Drug Information
              </Typography>
              {error && <Typography color='error'>{error}</Typography>}
              {success && (
                <Box
                  sx={{
									  backgroundColor: '#e8f5e9',
									  color: '#2e7d32',
									  padding: 2,
									  borderRadius: 1,
									  marginY: 2,
									  textAlign: 'center',
									  fontWeight: 500
                  }}
                >
                  <Typography>{success}</Typography>
                </Box>
              )}

              <form onSubmit={handleSubmit} encType='multipart/form-data'>
                <Grid container spacing={2}>
                  {Object.keys(drug).map((key) => {
									  if (key === 'image') {
									    return (
  <Grid item xs={6} sm={4} md={3} key={key}>
    <Button
      variant='contained'
      component='label'
      sx={{ backgroundColor: 'black', color: 'white' }}
    >
      Upload Image
      <input
        type='file'
        hidden
        name={key}
        onChange={handleFileChange}
      />
    </Button>
    {imagePreview && (
      <Box mt={2}>
        <img
          src={imagePreview}
          alt='Preview'
          style={{ maxWidth: '100%', maxHeight: '200px' }}
        />
      </Box>
    )}
  </Grid>
									    );
									  }

									  if (key === 'expiration_date') {
									    return (
  <Grid item xs={6} sm={4} md={3} key={key}>
    <TextField
      fullWidth
      label='Expiration Date'
      type='date'
      name={key}
      value={drug[key]}
      onChange={handleChange}
      InputLabelProps={{ shrink: true }}
      required
      sx={{
														  '& .MuiInputBase-root': { color: 'black' },
														  '& .MuiInputLabel-root': { color: 'black' },
														  '& .MuiOutlinedInput-notchedOutline': {
														    borderColor: 'black'
        }
      }}
    />
  </Grid>
									    );
									  }

									  if (key === 'category_ids') {
									    return (
  <Grid item xs={6} sm={4} md={3} key={key}>
    <TextField
      select
      fullWidth
      label='Category'
      name='category'
      value={selectedCategory?.id || ''}
      onChange={handleCategoryChange}
      required
      error={!drug.category_ids.length && touched}
      helperText={
															!drug.category_ids.length && touched
															  ? 'Category is required'
															  : ''
														}
      sx={{
														  '& .MuiInputBase-root': { color: 'black' },
														  '& .MuiInputLabel-root': { color: 'black' },
														  '& .MuiOutlinedInput-notchedOutline': {
														    borderColor: 'black'
        }
      }}
    >
      <MenuItem value=''>
        <em>Select a category</em>
      </MenuItem>
      {categories?.map((category) => (
        <MenuItem key={category?.id} value={category?.id}>
          {category?.name || 'Unnamed Category'}
        </MenuItem>
      ))}
      {!categories && (
        <MenuItem disabled value=''>
          <em>Loading categories...</em>
        </MenuItem>
      )}
    </TextField>
  </Grid>
									    );
									  }

									  if (key === 'currency') {
									    return (
  <Grid item xs={6} sm={4} md={3} key={key}>
    <TextField
      select
      fullWidth
      label='Currency'
      name={key}
      value={drug[key]?.code || drug[key] || ''}
      onChange={handleChange}
      required
      sx={{
														  '& .MuiInputBase-root': { color: 'black' },
														  '& .MuiInputLabel-root': { color: 'black' },
														  '& .MuiOutlinedInput-notchedOutline': {
														    borderColor: 'black'
        }
      }}
    >
      <MenuItem value=''>
        <em>Select a currency</em>
      </MenuItem>
      {Object.entries(currencyMapping)
														  .sort(([countryA], [countryB]) =>
														    countryA.localeCompare(countryB)
        )
														  .map(([country, currencyData]) => (
  <MenuItem
    key={currencyData.code}
    value={currencyData.code}
  >
    {`${country} - ${currencyData.code} `}
  </MenuItem>
														  ))}
    </TextField>
  </Grid>
									    );
									  }

									  return (
  <Grid item xs={6} sm={4} md={3} key={key}>
    <TextField
      fullWidth
      label={key
													  .split('_')
													  .map(
													    (word) =>
													      word.charAt(0).toUpperCase() + word.slice(1)
        )
													  .join(' ')}
      name={key}
      value={drug[key]}
      onChange={handleChange}
      type={
														key === 'price' ||
														key === 'cost_price' ||
														key === 'stock' ||
														key === 'reorder_level'
														  ? 'number'
														  : 'text'
													}
      required={[
													  'name',
													  'description',
													  'price',
													  'cost_price',
													  'stock',
													  'conditions_treated',
													  'contraindications',
													  'brand_name',
													  'packaging_info'
      ].includes(key)}
      sx={{
													  '& .MuiInputBase-root': { color: 'black' },
													  '& .MuiInputLabel-root': { color: 'black' },
													  '& .MuiOutlinedInput-notchedOutline': {
													    borderColor: 'black'
        }
      }}
    />
  </Grid>
									  );
                  })}
                </Grid>

                <Button
                  type='submit'
                  variant='contained'
                  sx={{
									  backgroundColor: '#2783f7', // Custom blue color
									  '&:hover': {
									    backgroundColor: '#1565c0'
                    },
									  marginTop: 2,
									  marginBottom: 1
                  }}
                  disabled={loading || isConverting}
                >
                  {loading
									  ? 'Processing...'
									  : isEditMode
									    ? 'Update Drug'
									    : 'Add Drug'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Add this near the end of your component, just before the closing tag */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DrugUploadForm;
